import {
  useCreate,
  useDelete,
  useGetList,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useEffect, useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { Issue } from "../../../../supabase/functions/_shared/automation/validateRule.ts";
import type { Trigger } from "../../../../supabase/functions/_shared/automation/types.ts";
import {
  conditionsEmpty,
  emptyConditions,
  toRule,
  TRIGGERS,
  validateDraft,
  type EditableRule,
} from "./ruleModel";
import { ConditionBuilder, selectClass } from "./ConditionBuilder";
import { ActionListEditor } from "./ActionListEditor";
import { DryRunPanel } from "./DryRunPanel";
import { useIssueText } from "./useIssueText";

type Props = {
  rule: EditableRule;
  readOnly: boolean;
  onSaved: () => void;
  onClose: () => void;
  now?: () => Date;
};
type Issues = { errors: Issue[]; warnings: Issue[] };
const defaultNow = () => new Date();

/** Rule editor (spec 05 §7). Saving is blocked while validateRule reports errors. */
export const RuleEditor = ({
  rule,
  readOnly,
  onSaved,
  onClose,
  now = defaultNow,
}: Props) => {
  const t = useTranslate();
  const notify = useNotify();
  const id = useId();
  const [draft, setDraft] = useState<EditableRule>(rule);
  const [issues, setIssues] = useState<Issues>({ errors: [], warnings: [] });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const { data: templates, error: templatesError } = useGetList(
    "email_templates",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: !readOnly, retry: false },
  );
  const [update] = useUpdate();
  const [create] = useCreate();
  const [remove] = useDelete();
  const set = (patch: Partial<EditableRule>) =>
    setDraft((d) => ({ ...d, ...patch }));
  const { errorText, warningText } = useIssueText();
  // A trigger change resets the conditions; non-empty ones need a confirmation first.
  const [pendingTrigger, setPendingTrigger] = useState<Trigger | null>(null);
  const triggerRef = useRef<HTMLSelectElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (pendingTrigger) confirmRef.current?.focus();
  }, [pendingTrigger]);
  const applyTrigger = (trigger: Trigger) =>
    set({ trigger, conditions: emptyConditions(trigger) });
  const requestTrigger = (trigger: Trigger) => {
    if (trigger === draft.trigger) return;
    if (conditionsEmpty(draft.trigger, draft.conditions)) applyTrigger(trigger);
    else setPendingTrigger(trigger);
  };
  const closeTriggerPrompt = (confirmed: boolean) => {
    if (confirmed && pendingTrigger) applyTrigger(pendingTrigger);
    setPendingTrigger(null);
    triggerRef.current?.focus();
  };

  const save = async () => {
    // The resource is optional (batch 3): without it, template names are not checked.
    const known =
      templatesError || !templates
        ? null
        : templates.map((x) => String(x.name));
    const result = validateDraft(draft, known);
    setIssues(result);
    if (result.errors.length) return;
    // Position belongs to the list's reorder: it is sent on create only, never on update.
    const data = {
      name: draft.name,
      description: draft.description,
      trigger: draft.trigger,
      conditions: draft.conditions,
      actions: draft.actions,
      enabled: draft.enabled,
    };
    setSaving(true);
    try {
      if (draft.id)
        await update(
          "automation_rules",
          { id: draft.id, data, previousData: rule },
          { mutationMode: "pessimistic", returnPromise: true },
        );
      else
        await create(
          "automation_rules",
          { data: { ...data, position: draft.position } },
          { returnPromise: true },
        );
      notify("gavion.automation.editor.saved", { type: "success" });
      onSaved();
    } catch {
      notify("gavion.automation.errors.save_failed", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    try {
      await remove(
        "automation_rules",
        { id: draft.id!, previousData: rule },
        { mutationMode: "pessimistic", returnPromise: true },
      );
      notify("gavion.automation.editor.deleted", { type: "success" });
      onSaved();
    } catch {
      notify("gavion.automation.errors.save_failed", { type: "error" });
    }
  };

  return (
    <div className="flex w-full max-w-4xl flex-col gap-4">
      <h2 className="text-xl font-extrabold break-words">
        {rule.name || t("gavion.automation.editor.title_new")}
      </h2>
      <fieldset disabled={readOnly} className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-name`}>
            {t("gavion.automation.editor.name")}
          </Label>
          <Input
            id={`${id}-name`}
            value={draft.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-desc`}>
            {t("gavion.automation.editor.description")}
          </Label>
          <Textarea
            id={`${id}-desc`}
            rows={2}
            value={draft.description ?? ""}
            onChange={(e) => set({ description: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-trigger`}>
            {t("gavion.automation.editor.trigger")}
          </Label>
          <select
            ref={triggerRef}
            id={`${id}-trigger`}
            className={`${selectClass} w-full sm:w-72`}
            value={draft.trigger}
            disabled={draft.is_default || readOnly || pendingTrigger !== null}
            onChange={(e) => requestTrigger(e.target.value as Trigger)}
          >
            {TRIGGERS.map((x) => (
              <option key={x} value={x}>
                {t(`gavion.automation.triggers.${x}`)}
              </option>
            ))}
          </select>
          {pendingTrigger && (
            <div
              role="alertdialog"
              aria-labelledby={`${id}-reset`}
              className="mt-2 flex flex-col gap-2 rounded-md border border-primary p-3 sm:flex-row sm:items-center"
            >
              <p id={`${id}-reset`} className="text-sm">
                {t("gavion.automation.editor.trigger_reset_confirm", {
                  trigger: t(`gavion.automation.triggers.${pendingTrigger}`),
                })}
              </p>
              <div className="flex gap-2">
                <Button
                  ref={confirmRef}
                  type="button"
                  size="sm"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={() => closeTriggerPrompt(true)}
                >
                  {t("gavion.automation.editor.trigger_reset_ok")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => closeTriggerPrompt(false)}
                >
                  {t("gavion.automation.editor.cancel")}
                </Button>
              </div>
            </div>
          )}
        </div>
        <h3 className="font-bold">
          {t("gavion.automation.editor.conditions")}
        </h3>
        <ConditionBuilder
          trigger={draft.trigger}
          value={draft.conditions}
          onChange={(conditions) => set({ conditions })}
        />
        <h3 className="font-bold">{t("gavion.automation.editor.actions")}</h3>
        <ActionListEditor
          value={draft.actions}
          onChange={(actions) => set({ actions })}
        />
      </fieldset>
      {issues.errors.map((i, k) => (
        <p key={`e-${k}`} role="alert" className="text-sm text-destructive">
          {errorText(i)}
        </p>
      ))}
      {issues.warnings.map((i, k) => (
        <p key={`w-${k}`} role="status" className="text-sm text-amber-700">
          {warningText(i)}
        </p>
      ))}
      <div className="flex flex-wrap gap-2">
        {!readOnly && (
          <Button
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            disabled={saving}
            aria-busy={saving}
            onClick={() => void save()}
          >
            {t("gavion.automation.editor.save")}
          </Button>
        )}
        {!readOnly && (
          <Button
            variant="outline"
            aria-expanded={testing}
            onClick={() => setTesting((x) => !x)}
          >
            {t("gavion.automation.editor.test")}
          </Button>
        )}
        {!readOnly && draft.id && !draft.is_default && (
          <Button variant="destructive" onClick={() => void onDelete()}>
            {t("gavion.automation.editor.delete")}
          </Button>
        )}
        <Button variant="ghost" onClick={onClose}>
          {t("gavion.automation.editor.close")}
        </Button>
      </div>
      {!readOnly && testing && <DryRunPanel rule={toRule(draft)} now={now} />}
    </div>
  );
};
